import { useState } from "react";
import { SmilePlus } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { QUICK_REACTIONS, type ReactionSummary } from "@/domains/messages/types";

interface ReactionBarProps {
  reactions: ReactionSummary[];
  onToggle: (emoji: string) => void;
  /** Hidden until row hover when there are no reactions yet (keeps the feed calm). */
  className?: string;
}

/**
 * Per-message reaction row: a count pill for every emoji used (highlighted when
 * the viewer is a reactor) plus an "add reaction" picker over the fixed
 * QUICK_REACTIONS palette.
 */
export function ReactionBar({ reactions, onToggle, className }: ReactionBarProps) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)}>
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          onClick={() => onToggle(r.emoji)}
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors",
            r.reactedByMe
              ? "border-primary/40 bg-primary/10 text-foreground"
              : "border-border bg-muted/40 text-muted-foreground hover:bg-muted"
          )}
        >
          <span>{r.emoji}</span>
          <span className="tabular-nums">{r.count}</span>
        </button>
      ))}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t.messages.addReaction}
            className={cn(
              "inline-flex h-[22px] w-[22px] items-center justify-center rounded-full text-muted-foreground",
              "opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100",
              "data-[state=open]:opacity-100"
            )}
          >
            <SmilePlus className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-1">
          <div className="flex items-center gap-0.5">
            {QUICK_REACTIONS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  onToggle(emoji);
                  setOpen(false);
                }}
                className="rounded p-1 text-base leading-none hover:bg-muted"
              >
                {emoji}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
